'use client'

import dynamic from 'next/dynamic';
import Grid from '@mui/material/Grid2';
import { Alert, Box, Button, Typography } from '@mui/material';
import { usePathname, useRouter } from 'next/navigation';
import React from 'react';
import { FundingProvider, useFunding } from '@/context/funding';
import { FundingStatus } from '@/types/funding';
import { useTranslations } from 'next-intl';

const PageContainer = dynamic(() => import('@/components/common/page-container'), { ssr: false });
const StepsCard = dynamic(() => import('@/components/pages/funding/steps-card/steps-card'), { ssr: false });
const MainSection = dynamic(() => import('@/components/pages/funding/main-section/main-section'), { ssr: false });
const ModalUpgrade = dynamic(() => import('@/components/funding/ModalUpgrade'), { ssr: false });
const MessageSendingEmail = dynamic(() => import('@/components/funding/MessageSendingEmail'), { ssr: false });
const LoadingGenerate = dynamic(() => import('@/components/funding/LoadingGenerate'), { ssr: false });
const FundingTutorialJoyride = dynamic(() => import('@/components/widgets/joyride/funding-tutorial-joyride'), { ssr: false });
import '@/components/styles/funding/common-funding.scss';
// Helper function to determine ModalUpgrade title based on upgrade conditions
function determineUpgradeTitle(fundingStatus: FundingStatus | null): number {
    if (!fundingStatus) return 2; // Default fallback

    if (fundingStatus.need_upgrade_trial_finished) return 0;
    if (fundingStatus.need_upgrade_package_finished) return 1;
    if (fundingStatus.need_upgrade_date_finished) return 2;

    return 2; // Default fallback
}

// Inner component to access funding context
function LayoutContent({ children }: { children: React.ReactNode }) {
    const pathname = usePathname();
    const router = useRouter();
    const t = useTranslations('funding');
    const {
        GetFundingStatus,
        showUpgradeModal,
        upgradeModalTitle,
        fundingStatus,
        statusCheckError,
        showLoadingGenerateModal,
        showMessageSendingModal,
        setShowMessageSendingModal,
        setShowUpgradeModal,
        fundingTourState,
        completeFundingTour,
        skipFundingTour,
    } = useFunding();

    // Fetch funding status on every page navigation
    React.useEffect(() => {
        GetFundingStatus();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pathname]); // Run on every pathname change (GetFundingStatus excluded to prevent infinite loop)

    return (
        <PageContainer
            className={"__funding_layout__"}
        >
            {/* Funding Tutorial Tour */}
            {pathname !== '/funding' && (
                <FundingTutorialJoyride
                    run={fundingTourState.run}
                    onComplete={completeFundingTour}
                    onSkip={skipFundingTour}
                    stepIndex={fundingTourState.stepIndex}
                    tourKey={fundingTourState.tourKey}
                />
            )}

            {/* Error State Display - Show when status check fails after retry */}
            {statusCheckError && (
                <Box sx={{ mb: 3, width: '100%' }}>
                    <Alert
                        severity="error"
                        action={
                            <Button
                                color="inherit"
                                size="small"
                                onClick={() => {
                                    window.location.reload();
                                }}
                            >
                                {t('errors.reload_page')}
                            </Button>
                        }
                    >
                        <Typography variant="body1" fontWeight={600}>
                            {t('errors.status_check_failed')}
                        </Typography>
                        <Typography variant="body2" sx={{ mt: 1 }}>
                            {statusCheckError}
                        </Typography>
                        <Typography variant="caption" sx={{ mt: 1, display: 'block' }}>
                            {t('errors.status_check_help')}
                        </Typography>
                    </Alert>
                </Box>
            )}

            {/* Disable interactions when error exists */}
            <Box sx={{
                opacity: statusCheckError ? 0.5 : 1,
                pointerEvents: statusCheckError ? 'none' : 'auto',
                position: 'relative'
            }}>
                <Grid
                    width={'100%'}
                    container
                    spacing={3}
                    paddingTop={3}
                >

                    <Grid
                        size={{ lg: 8.4, md: 12, sm: 12 }}
                    >
                        {
                            // Landing page (/funding) renders directly without tabs
                            // All sub-pages (/funding/add-professor, etc.) use MainSection wrapper with TabBar
                            pathname === '/funding' ?

                                children
                                :
                                <MainSection>
                                    {children}
                                </MainSection>
                        }

                    </Grid>

                    {
                        pathname !== '/funding' && <Grid
                            size={{ lg: 3.6, md: 12, sm: 12 }}
                        >
                            <div className=' sticky top-3 '>
                                <StepsCard />
                            </div>
                        </Grid>
                    }
                </Grid>

                {/* ModalUpgrade - Display when upgrade is needed */}
                {showUpgradeModal && (
                    <ModalUpgrade
                        title={upgradeModalTitle}
                        onClose={() => {
                            setShowUpgradeModal(false);
                            // Redirect trial users back to preview when closing modal on followup page
                            if (pathname === '/funding/followup' && fundingStatus?.is_trial) {
                                router.push('/funding/preview');
                            }
                        }}
                    />
                )}

                {/* LoadingGenerate - Display when generating preview */}
                {showLoadingGenerateModal && (
                    <LoadingGenerate open={showLoadingGenerateModal} />
                )}

                {/* MessageSendingEmail - Display when email is sent successfully */}
                {showMessageSendingModal && (
                    <MessageSendingEmail
                        open={showMessageSendingModal}
                        onClose={() => {
                            setShowMessageSendingModal(false);
                            router.push('/funding/followup');
                        }}
                    />
                )}
            </Box>
        </PageContainer>
    );
}

export default function RootLayout({
    children,
}: Readonly<{
    children: React.ReactNode
}>) {
    return (
        <FundingProvider>
            <LayoutContent>{children}</LayoutContent>
        </FundingProvider>
    );
};
